import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';

import AddEditForm from '../../../sharedComponents/AddEditForm';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import useManageWarehouseReceivingAddresses from './useManageWarehouseReceivingAddresses';
import useGetCustomerProfile from '../../../hooks/useGetCustomerProfile';
import ProfileCombo from '../../../sharedComponents/ProfileCombo';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '640px',
    maxHeight: '90vh',
    overflow: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

const formItems = [
  { title: 'Address Nickname', name: 'addressNickName', type: 'text' },
  { title: 'Address', name: 'address', type: 'textarea' },
];

const initialValue = {
  id: null,
  addressNickName: '',
  address: '',
};

const WarehouseReceivingAddressSetting = () => {
  const { profiles, getCustomerProfiles } = useGetCustomerProfile();
  const [mode, setMode] = useState('register');
  const [modalTitle, setModalTitle] = useState('Add Warehouse Receiving Address');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selected, setSelected] = useState(initialValue);
  const [assignTargetId, setAssignTargetId] = useState(null);
  const [profileComboKey, setProfileComboKey] = useState(0);

  const { addresses, isLoading, error, refreshAll, create, update, remove, assignCustomer, unassignCustomer } =
    useManageWarehouseReceivingAddresses();

  const tableRows = useMemo(() => addresses || [], [addresses]);

  useEffect(() => {
    if (error) {
      const msg = error?.response?.data?.message || error?.message || 'An unexpected error occurred.';
      toast.error(msg);
    }
  }, [error]);

  useEffect(() => {
    getCustomerProfiles();
    refreshAll();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSelected({ ...selected, [name]: type === 'checkbox' ? checked : value });
  };

  const openForm = (isEdit, id) => {
    if (isEdit) {
      const row = tableRows.find((a) => String(a.id) === String(id));
      if (row) {
        setSelected({
          id: row.id,
          addressNickName: row.addressNickName || '',
          address: row.address || '',
        });
        setMode('edit');
        setModalTitle('Edit Warehouse Receiving Address');
      }
    } else {
      setSelected({ ...initialValue });
      setMode('register');
      setModalTitle('Add Warehouse Receiving Address');
    }
    setIsFormOpen(true);
  };

  const closeForm = () => setIsFormOpen(false);

  const openConfirm = (id) => {
    const row = tableRows.find((a) => String(a.id) === String(id));
    if (row) {
      setSelected({
        id: row.id,
        addressNickName: row.addressNickName || '',
        address: row.address || '',
      });
      setIsConfirmOpen(true);
    }
  };

  const closeConfirm = () => setIsConfirmOpen(false);

  const handleRegister = async () => {
    try {
      await create({
        addressNickName: selected.addressNickName,
        address: selected.address,
      });
      toast.success('Address added!');
      closeForm();
    } catch (e) {
      // handled by error effect
    }
  };

  const handleUpdate = async () => {
    try {
      await update(selected.id, {
        addressNickName: selected.addressNickName,
        address: selected.address,
      });
      toast.success('Address updated!');
      closeForm();
    } catch (e) {
      // handled by error effect
    }
  };

  const handleDelete = async () => {
    try {
      await remove(selected.id);
      toast.success('Address deleted!');
      closeConfirm();
    } catch (e) {
      // handled by error effect
    }
  };

  // ---- Assign / Unassign ----
  const openAssignModal = (addressId) => {
    setAssignTargetId(addressId);
    setProfileComboKey((k) => k + 1);
    setIsAssignOpen(true);
  };

  const handleAssign = async (smileName) => {
    const match = (profiles || []).find((p) => p.smileName === smileName);
    if (!match) return;
    try {
      await assignCustomer(assignTargetId, match.id);
      toast.success(`Assigned ${match.contact || match.smileName}`);
    } catch (e) {
      // handled by error effect
    }
  };

  const handleUnassign = async (addressId, customerId) => {
    try {
      await unassignCustomer(addressId, customerId);
      toast.success('Customer unassigned');
    } catch (e) {
      // handled by error effect
    }
  };

  const confirmItems = [
    { title: 'Address Nickname', name: 'addressNickName', type: 'text' },
    { title: 'Address', name: 'address', type: 'textarea' },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Add / Edit modal */}
      <Modal isOpen={isFormOpen} onRequestClose={closeForm} style={modalStyles} ariaHideApp={false}>
        <AddEditForm
          items={formItems}
          name={modalTitle}
          data={selected}
          mode={mode}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleRegister={handleRegister}
          handleUpdate={handleUpdate}
          stateValue={selected}
          submitting={isLoading}
        />
      </Modal>

      {/* Delete confirm modal */}
      <Modal isOpen={isConfirmOpen} onRequestClose={closeConfirm} style={modalStyles} ariaHideApp={false}>
        <DeleteConfirm
          items={confirmItems}
          data={selected}
          closeConfirm={closeConfirm}
          confirmFunction={handleDelete}
        />
      </Modal>

      {/* Assign customer modal */}
      <Modal isOpen={isAssignOpen} onRequestClose={() => setIsAssignOpen(false)} style={modalStyles} ariaHideApp={false}>
        <div className="p-4">
          <h3 className="text-xl font-bold mb-4">Assign Customer to Address</h3>
          <p className="text-sm text-gray-600 mb-3">Search and select a customer to assign</p>
          <ProfileCombo
            key={profileComboKey}
            profiles={profiles || []}
            onChange={handleAssign}
            initialSmileName=""
          />
          <button
            onClick={() => setIsAssignOpen(false)}
            className="mt-4 px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </Modal>

      <div className="mt-4 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-4xl">Warehouse Receiving Address</h2>
          <button
            onClick={() => openForm(false, null)}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Add New
          </button>
        </div>

        <div className="overflow-y-auto shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  No
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Address Nickname
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Address
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Assigned Customers
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {tableRows.map((row, index) => (
                <tr key={row.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {index + 1}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{row.addressNickName}</td>
                  <td className="px-3 py-4 text-sm text-gray-700 whitespace-pre-wrap">{row.address}</td>
                  <td className="px-3 py-4 text-sm text-gray-700">
                    <div className="flex flex-wrap gap-1 items-center">
                      {(row.customers || []).map((c) => {
                        const isTama = c.shop === 'tama';
                        const chipClass = isTama
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-emerald-100 text-emerald-800';
                        return (
                          <span
                            key={c.customerId}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${chipClass}`}
                          >
                            {c.contact || c.smileName || `#${c.customerId}`}
                            <XMarkIcon
                              onClick={() => handleUnassign(row.id, c.customerId)}
                              className="w-3 h-3 cursor-pointer hover:text-red-600"
                            />
                          </span>
                        );
                      })}
                      <button
                        onClick={() => openAssignModal(row.id)}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-0 text-right text-sm font-medium">
                    <div className="flex items-center space-x-3 justify-end">
                      <PencilIcon
                        onClick={() => openForm(true, row.id)}
                        className="cursor-pointer w-6 h-6 fill-green-600"
                      />
                      <TrashIcon
                        onClick={() => openConfirm(row.id)}
                        className="cursor-pointer w-6 h-6 fill-red-600"
                      />
                    </div>
                  </td>
                </tr>
              ))}

              {tableRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-6 text-sm text-gray-500">
                    No addresses yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WarehouseReceivingAddressSetting;
